<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatCentsAsCurrency } from '@/utils/currencyFormat'
import { useAuth } from '@/composables/useAuth'
import { useEvents } from '@/composables/useEvents'
import { useDashboardStats } from '@/composables/useDashboardStats'
import { useSupportUnreadCount } from '@/composables/useSupportUnreadCount'
import { useCollapsedIds } from '@/composables/useCollapsedIds'
import { useLocale } from '@/composables/useLocale'
import type { Event, Race } from '@/types/events'
import type { RecentRegistration, RecentCancellation } from '@/types/dashboard'
import OrganizerLayout from '@/components/layout/OrganizerLayout.vue'
import AppSpinner from '@/components/shared/AppSpinner.vue'
import HeaderCreateButton from '@/components/shared/HeaderCreateButton.vue'
import PublicLinksCard from '@/components/organizer/events/PublicLinksCard.vue'

const { t, locale } = useI18n()
const { currentLocale } = useLocale()
const { user, currentOrganisation } = useAuth()
const { events, loading, error, fetchEvents } = useEvents()
const { dashboardStats, statsLoading, fetchDashboardStats } = useDashboardStats()
const { fetch: fetchUnreadCount } = useSupportUnreadCount()

const MAX_DASHBOARD_EVENTS = 8

onMounted(() => {
  void fetchEvents()
  void fetchDashboardStats()
  const orgId = currentOrganisation.value?.id
  if (orgId) {
    void fetchUnreadCount(orgId)
  }
})

const totalEvents = computed(() => events.value.length)

const openRacesCount = computed(() =>
  events.value.reduce((count: number, event: Event) => {
    const races = event.active_edition?.races ?? []
    return count + races.filter((r) => r.registrations_open).length
  }, 0),
)

function eventSortPriority(event: Event): number {
  if (event.maintenance) return 3
  if (!event.active_edition) return 2
  const hasOpenRace = (event.active_edition.races ?? []).some((r) => r.registrations_open)
  return hasOpenRace ? 0 : 1
}

const sortedEvents = computed(() =>
  [...events.value].sort((a, b) => eventSortPriority(a) - eventSortPriority(b)),
)

function isWaitingListActive(race: Race): boolean {
  return (
    race.allow_waiting_list &&
    race.max_participants !== null &&
    race.participant_count >= race.max_participants
  )
}

const displayedEvents = computed(() => sortedEvents.value.slice(0, MAX_DASHBOARD_EVENTS))

const hasMoreEvents = computed(() => events.value.length > MAX_DASHBOARD_EVENTS)

// Dates arrive as `YYYY-MM-DD` (optionally with a time component). The day,
// month name and year are read/formatted with timeZone: 'UTC' throughout so
// the displayed day never shifts under negative UTC offsets (ISO date-only
// strings are parsed as UTC midnight by `Date`). The day/month/year order is
// assembled explicitly (day first) rather than left to the locale's default
// Intl ordering, since plain 'en' resolves to US month-first formatting.
function formatLongDate(dateStr: string): string {
  const date = new Date(dateStr)
  const day = date.getUTCDate()
  const year = date.getUTCFullYear()
  const month = new Intl.DateTimeFormat(currentLocale.value, {
    month: 'long',
    timeZone: 'UTC',
  }).format(date)
  return `${day} ${month} ${year}`
}

function formatDateRange(startDate: string, endDate: string): string {
  const startStr = formatLongDate(startDate)
  if (startDate === endDate) return startStr
  const endStr = formatLongDate(endDate)
  return `${startStr} - ${endStr}`
}

function eventInitial(name: string): string {
  return name.charAt(0).toUpperCase()
}

function formatCurrency(value: string): string {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'EUR' }).format(
    parseFloat(value),
  )
}

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffSeconds = Math.floor(diffMs / 1000)
  const diffMinutes = Math.floor(diffSeconds / 60)
  const diffHours = Math.floor(diffMinutes / 60)
  const diffDays = Math.floor(diffHours / 24)

  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })

  if (diffDays > 0) return rtf.format(-diffDays, 'day')
  if (diffHours > 0) return rtf.format(-diffHours, 'hour')
  if (diffMinutes > 0) return rtf.format(-diffMinutes, 'minute')
  return rtf.format(-diffSeconds, 'second')
}

function registrationInitial(reg: RecentRegistration): string {
  return reg.participant_name.charAt(0).toUpperCase()
}

function cancellationInitial(reg: RecentCancellation): string {
  return reg.participant_name.charAt(0).toUpperCase()
}

const linksOpenEventId = ref<number | null>(null)

function toggleLinks(eventId: number): void {
  linksOpenEventId.value = linksOpenEventId.value === eventId ? null : eventId
}

const orgSlug = computed<string | null>(
  () => currentOrganisation.value?.slug ?? null,
)

// Reused from the sidebar (SidebarEventZone.vue / SidebarEditionZone.vue) so the "Your events"
// section speaks the same visual language for editions and races.
const LAYERS_ICON =
  'M6 6.878V6a2.25 2.25 0 012.25-2.25h7.5A2.25 2.25 0 0118 6v.878m-12 0c.235-.083.487-.128.75-.128h10.5c.263 0 .515.045.75.128m-12 0A2.25 2.25 0 004.5 9v.878m13.5-3A2.25 2.25 0 0119.5 9v.878m0 0a2.246 2.246 0 00-.75-.128H5.25c-.263 0-.515.045-.75.128m15 0A2.25 2.25 0 0121 12v6a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 18v-6c0-.98.626-1.813 1.5-2.122'
const FLAG_ICON =
  'M3 3v1.5M3 21v-6m0 0l2.77-.693a9 9 0 016.208.682l.108.054a9 9 0 006.086.71l3.114-.732a48.524 48.524 0 01-.005-10.499l-3.11.732a9 9 0 01-6.085-.711l-.108-.054a9 9 0 00-6.208-.682L3 4.5M3 15V4.5'

const { isCollapsed: isEventCollapsed, toggle: toggleEventCollapsed } =
  useCollapsedIds('dashboard_collapsed_events')
const { isCollapsed: isEditionCollapsed, toggle: toggleEditionCollapsed } =
  useCollapsedIds('dashboard_collapsed_editions')
</script>

<template>
  <OrganizerLayout>

    <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <!-- Heading -->
      <div class="flex items-center justify-between mb-8">
        <div>
          <h1 class="text-3xl font-bold text-white">
            {{ t('dashboard.organizer.welcome_heading', { name: user?.first_name ?? '' }) }}
          </h1>
          <p class="text-slate-400 mt-1">{{ t('dashboard.organizer.welcome_subheading') }}</p>
        </div>
        <HeaderCreateButton
          v-if="!loading && events.length > 0"
          to="/organizer/events/create"
          :label="t('events.list.create_button')"
        />
      </div>

      <!-- Loading state -->
      <AppSpinner v-if="loading" />

      <!-- Error state -->
      <div
        v-else-if="error"
        class="mb-6 rounded-xl bg-red-500/10 border border-red-500/30 px-5 py-3 text-sm text-red-400"
      >
        {{ t('events.list.error_load') }}
      </div>

      <!-- Empty state: onboarding checklist -->
      <div
        v-else-if="events.length === 0"
        class="rounded-2xl border border-dashed border-slate-700 bg-slate-800/30 px-8 py-12"
      >
        <div class="max-w-md mx-auto text-center">
          <div class="mx-auto mb-4 w-14 h-14 rounded-full bg-slate-800 flex items-center justify-center">
            <svg class="w-7 h-7 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <h2 class="text-xl font-bold text-white mb-1">{{ t('dashboard.organizer.onboarding_title') }}</h2>
          <p class="text-slate-400 text-sm mb-8">{{ t('dashboard.organizer.onboarding_subtitle') }}</p>

          <ol class="text-left space-y-4">
            <li class="flex items-start gap-3">
              <span class="shrink-0 w-6 h-6 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center text-xs font-bold text-emerald-400">1</span>
              <div>
                <RouterLink
                  to="/organizer/events/create"
                  class="text-sm font-semibold text-emerald-400 hover:text-emerald-300 transition-colors"
                >
                  {{ t('dashboard.organizer.onboarding_step1') }}
                </RouterLink>
              </div>
            </li>
            <li class="flex items-start gap-3">
              <span class="shrink-0 w-6 h-6 rounded-full bg-slate-700/50 border border-slate-600 flex items-center justify-center text-xs font-bold text-slate-500">2</span>
              <span class="text-sm text-slate-500">{{ t('dashboard.organizer.onboarding_step2') }}</span>
            </li>
            <li class="flex items-start gap-3">
              <span class="shrink-0 w-6 h-6 rounded-full bg-slate-700/50 border border-slate-600 flex items-center justify-center text-xs font-bold text-slate-500">3</span>
              <span class="text-sm text-slate-500">{{ t('dashboard.organizer.onboarding_step3') }}</span>
            </li>
          </ol>

          <div class="mt-8">
            <RouterLink
              to="/organizer/events/create"
              class="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white font-semibold rounded-lg px-5 py-2.5 text-sm transition-colors"
            >
              {{ t('events.list.create_button') }}
            </RouterLink>
          </div>
        </div>
      </div>

      <!-- Dashboard content: stats + event list + recent registrations -->
      <template v-else>
        <!-- Zone A: Stats header strip -->
        <div class="mb-8 space-y-4">
          <dl class="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div class="rounded-2xl border border-slate-700 bg-slate-800/50 px-6 py-6">
              <dd class="text-3xl font-bold text-white">{{ totalEvents }}</dd>
              <dt class="text-sm text-slate-400 mt-1">{{ t('dashboard.organizer.stats_total_events') }}</dt>
            </div>
            <div class="rounded-2xl border border-slate-700 bg-slate-800/50 px-6 py-6">
              <dd class="text-3xl font-bold text-white">{{ openRacesCount }}</dd>
              <dt class="text-sm text-slate-400 mt-1">{{ t('dashboard.organizer.stats_open_races') }}</dt>
            </div>
            <div class="rounded-2xl border border-slate-700 bg-slate-800/50 px-6 py-6">
              <dd class="text-3xl font-bold text-white">
                <template v-if="statsLoading">
                  <span class="inline-block w-16 h-8 bg-slate-700 rounded animate-pulse"></span>
                </template>
                <template v-else>
                  {{ dashboardStats?.registration_count_this_month ?? '--' }}
                </template>
              </dd>
              <dt class="text-sm text-slate-400 mt-1">{{ t('dashboard.organizer.stats_registrations_this_month') }}</dt>
            </div>
            <div class="rounded-2xl border border-slate-700 bg-slate-800/50 px-6 py-6">
              <dd class="text-3xl font-bold text-white">
                <template v-if="statsLoading">
                  <span class="inline-block w-20 h-8 bg-slate-700 rounded animate-pulse"></span>
                </template>
                <template v-else>
                  {{ dashboardStats ? formatCurrency(dashboardStats.revenue_this_month) : '--' }}
                </template>
              </dd>
              <dt class="text-sm text-slate-400 mt-1">{{ t('dashboard.organizer.stats_revenue_this_month') }}</dt>
            </div>
          </dl>

        </div>

        <!-- Zone B: Event list table -->
        <section :aria-label="t('dashboard.organizer.your_events')">
          <h2 class="text-lg font-semibold text-white mb-4">{{ t('dashboard.organizer.your_events') }}</h2>

          <div class="space-y-3">
            <article
              v-for="ev in displayedEvents"
              :key="ev.id"
              class="rounded-xl border border-slate-700 overflow-hidden"
            >
              <!-- Event header row -->
              <div class="flex items-center gap-4 px-5 py-4 bg-slate-800/60">
                <div class="shrink-0">
                  <img
                    v-if="ev.logo_url"
                    :src="ev.logo_url"
                    :alt="ev.name"
                    class="w-9 h-9 rounded-lg object-cover"
                  />
                  <div
                    v-else
                    class="w-9 h-9 rounded-lg bg-slate-700 flex items-center justify-center"
                  >
                    <span class="text-sm font-bold text-slate-300">{{ eventInitial(ev.name) }}</span>
                  </div>
                </div>
                <div class="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
                  <RouterLink
                    :to="`/organizer/events/${ev.slug}`"
                    class="text-sm font-semibold text-white hover:text-emerald-400 transition-colors"
                  >
                    {{ ev.name }}
                  </RouterLink>
                  <span
                    v-if="ev.maintenance"
                    class="inline-flex items-center rounded-full bg-amber-500/15 border border-amber-500/30 px-2 py-0.5 text-xs font-medium text-amber-400"
                  >
                    {{ t('events.list.maintenance_badge') }}
                  </span>
                </div>
                <RouterLink
                  :to="`/organizer/events/${ev.slug}`"
                  class="shrink-0 text-xs font-medium text-emerald-400 hover:text-emerald-300 transition-colors px-3 py-1.5 rounded-lg border border-emerald-500/30 hover:border-emerald-500/60"
                >
                  {{ t('dashboard.organizer.manage_event') }}
                  <span class="sr-only">{{ t('dashboard.organizer.manage_event_sr', { name: ev.name }) }}</span>
                </RouterLink>
                <button
                  type="button"
                  class="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-lg border transition-colors"
                  :class="linksOpenEventId === ev.id
                    ? 'border-slate-500 bg-slate-700 text-white'
                    : 'border-slate-600 text-slate-400 hover:border-slate-400 hover:text-white'"
                  :title="t('events.public_links.toggle_title')"
                  :aria-label="t('events.public_links.toggle_title')"
                  :aria-expanded="linksOpenEventId === ev.id"
                  @click="toggleLinks(ev.id)"
                >
                  <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                </button>
                <button
                  v-if="ev.active_edition"
                  type="button"
                  class="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-lg border border-slate-600 text-slate-400 hover:border-slate-400 hover:text-white transition-colors"
                  :title="isEventCollapsed(ev.id)
                    ? t('dashboard.organizer.expand_event', { name: ev.name })
                    : t('dashboard.organizer.collapse_event', { name: ev.name })"
                  :aria-label="isEventCollapsed(ev.id)
                    ? t('dashboard.organizer.expand_event', { name: ev.name })
                    : t('dashboard.organizer.collapse_event', { name: ev.name })"
                  :aria-expanded="!isEventCollapsed(ev.id)"
                  :aria-controls="`dashboard-event-${ev.id}-body`"
                  @click="toggleEventCollapsed(ev.id)"
                >
                  <svg
                    class="w-3.5 h-3.5 transition-transform"
                    :class="isEventCollapsed(ev.id) ? '-rotate-90' : 'rotate-0'"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>

              <!-- Active edition + races -->
              <div v-if="ev.active_edition" class="bg-slate-800/30">
                <div v-show="!isEventCollapsed(ev.id)" :id="`dashboard-event-${ev.id}-body`">
                  <!-- Edition row -->
                  <div class="flex items-center gap-4 pl-8 pr-5 py-3 border-t border-slate-700/40">
                    <div class="flex-1 min-w-0 flex items-center gap-5">
                      <span class="flex-1 min-w-0 flex items-center gap-2">
                        <svg class="w-4 h-4 text-slate-500 shrink-0" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24" aria-hidden="true">
                          <path stroke-linecap="round" stroke-linejoin="round" :d="LAYERS_ICON" />
                        </svg>
                        <RouterLink
                          :to="`/organizer/events/${ev.slug}/editions/${ev.active_edition.slug}`"
                          class="text-sm font-medium text-slate-200 hover:text-emerald-400 transition-colors truncate min-w-0"
                        >
                          {{ ev.active_edition.name }}
                        </RouterLink>
                      </span>
                      <span class="hidden md:block text-xs text-slate-500 shrink-0 whitespace-nowrap">
                        {{ formatDateRange(ev.active_edition.start_date, ev.active_edition.end_date) }}
                      </span>
                    </div>
                    <span class="hidden sm:flex items-center gap-1.5 text-xs text-slate-400 shrink-0 w-16 justify-end">
                      <svg class="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {{ ev.active_edition.participant_count }}
                    </span>
                    <span class="hidden md:block text-xs font-semibold text-white shrink-0 w-20 text-right">
                      {{ formatCurrency(ev.active_edition.total_revenue) }}
                    </span>
                    <RouterLink
                      :to="`/organizer/events/${ev.slug}/editions/${ev.active_edition.slug}/participants`"
                      class="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-md border border-slate-600 hover:border-slate-400 text-slate-400 hover:text-white transition-colors"
                      :title="t('nav.participants')"
                      :aria-label="t('nav.participants')"
                    >
                      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </RouterLink>
                    <button
                      v-if="ev.active_edition.races?.length"
                      type="button"
                      class="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-md border border-slate-600 text-slate-400 hover:border-slate-400 hover:text-white transition-colors"
                      :title="isEditionCollapsed(ev.active_edition.id)
                        ? t('dashboard.organizer.expand_edition', { name: ev.active_edition.name })
                        : t('dashboard.organizer.collapse_edition', { name: ev.active_edition.name })"
                      :aria-label="isEditionCollapsed(ev.active_edition.id)
                        ? t('dashboard.organizer.expand_edition', { name: ev.active_edition.name })
                        : t('dashboard.organizer.collapse_edition', { name: ev.active_edition.name })"
                      :aria-expanded="!isEditionCollapsed(ev.active_edition.id)"
                      :aria-controls="`dashboard-edition-${ev.active_edition.id}-races`"
                      @click="toggleEditionCollapsed(ev.active_edition.id)"
                    >
                      <svg
                        class="w-3.5 h-3.5 transition-transform"
                        :class="isEditionCollapsed(ev.active_edition.id) ? '-rotate-90' : 'rotate-0'"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>

                  <!-- Race rows -->
                  <div
                    v-if="ev.active_edition.races?.length"
                    v-show="!isEditionCollapsed(ev.active_edition.id)"
                    :id="`dashboard-edition-${ev.active_edition.id}-races`"
                  >
                    <div
                      v-for="race in ev.active_edition.races"
                      :key="race.id"
                      class="flex items-center gap-4 pl-12 pr-5 py-2.5 border-t border-slate-700/20"
                    >
                      <!-- Name + status badge (grouped) -->
                      <div class="flex-1 min-w-0 flex items-center gap-5">
                        <span class="flex-1 min-w-0 flex items-center gap-2">
                          <svg class="w-4 h-4 text-slate-500 shrink-0" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24" aria-hidden="true">
                            <path stroke-linecap="round" stroke-linejoin="round" :d="FLAG_ICON" />
                          </svg>
                          <RouterLink
                            :to="`/organizer/events/${ev.slug}/editions/${ev.active_edition.slug}/races/${race.slug}/edit`"
                            class="text-sm text-slate-300 hover:text-emerald-400 transition-colors truncate min-w-0"
                          >
                            {{ race.name }}
                          </RouterLink>
                        </span>
                        <span
                          v-if="isWaitingListActive(race)"
                          class="shrink-0 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-amber-500/15 border border-amber-500/30 text-amber-400"
                        >
                          <span
                            class="w-1.5 h-1.5 rounded-full bg-amber-400"
                            aria-hidden="true"
                          ></span>
                          {{ t('dashboard.organizer.race_waiting_list') }}
                        </span>
                        <span
                          v-else
                          class="shrink-0 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium"
                          :class="race.registrations_open
                            ? 'bg-emerald-500/15 border border-emerald-500/30 text-emerald-400'
                            : 'bg-slate-700/50 border border-slate-600 text-slate-400'"
                        >
                          <span
                            class="w-1.5 h-1.5 rounded-full"
                            :class="race.registrations_open ? 'bg-emerald-400' : 'bg-slate-500'"
                            aria-hidden="true"
                          ></span>
                          {{ race.registrations_open ? t('dashboard.organizer.race_open') : t('dashboard.organizer.race_closed') }}
                        </span>
                      </div>
                      <!-- Base price -->
                      <span class="hidden sm:block text-xs text-slate-400 shrink-0 w-24 text-right" :title="race.pricing.current_tier?.name">
                        {{ formatCentsAsCurrency(race.pricing.current_price_cents, race.pricing.currency, locale) }}
                        <span v-if="race.pricing.current_tier" class="block truncate text-slate-500" data-testid="race-tier-name">{{ race.pricing.current_tier.name }}</span>
                      </span>
                      <!-- Participants / max -->
                      <span class="hidden sm:block text-xs text-slate-300 shrink-0 w-20 text-right">
                        {{ race.participant_count }}&thinsp;/&thinsp;{{ race.max_participants ?? '∞' }}
                      </span>
                      <!-- Revenue -->
                      <span class="hidden md:block text-xs font-semibold text-white shrink-0 w-20 text-right">
                        {{ formatCurrency(race.total_revenue) }}
                      </span>
                      <!-- Participants action button -->
                      <RouterLink
                        :to="`/organizer/events/${ev.slug}/editions/${ev.active_edition.slug}/races/${race.slug}/participants`"
                        class="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-md border border-slate-600 hover:border-slate-400 text-slate-400 hover:text-white transition-colors"
                        :title="t('nav.participants')"
                        :aria-label="t('nav.participants')"
                      >
                        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </RouterLink>
                    </div>
                  </div>
                </div>

                <!-- Public links panel: independent of the collapse state above -->
                <div
                  v-if="linksOpenEventId === ev.id"
                  class="px-5 py-3 border-t border-slate-700/40"
                >
                  <PublicLinksCard :event="ev" :org-slug="orgSlug" />
                </div>
              </div>

              <!-- No active edition -->
              <div v-else class="px-5 py-3 bg-slate-800/30 border-t border-slate-700/40">
                <p class="text-xs text-slate-500">{{ t('dashboard.organizer.no_active_edition') }}</p>
              </div>
            </article>
          </div>

          <!-- View all events link -->
          <div v-if="hasMoreEvents" class="mt-4 text-center">
            <RouterLink
              to="/organizer/events"
              class="text-sm font-medium text-emerald-400 hover:text-emerald-300 transition-colors"
            >
              {{ t('dashboard.organizer.view_all_events', { count: totalEvents }) }} &rarr;
            </RouterLink>
          </div>
        </section>

        <!-- Zone C: Recent registrations feed -->
        <section class="mt-8" :aria-label="t('dashboard.organizer.recent_registrations_title')">
          <h2 class="text-lg font-semibold text-white mb-4">{{ t('dashboard.organizer.recent_registrations_title') }}</h2>

          <!-- Loading state for recent registrations -->
          <div v-if="statsLoading" class="space-y-3">
            <div
              v-for="n in 3"
              :key="n"
              class="rounded-xl bg-slate-800/50 border border-slate-700 px-5 py-4"
            >
              <div class="flex items-center gap-4">
                <div class="w-10 h-10 rounded-full bg-slate-700 animate-pulse"></div>
                <div class="flex-1 space-y-2">
                  <div class="h-4 w-32 bg-slate-700 rounded animate-pulse"></div>
                  <div class="h-3 w-48 bg-slate-700 rounded animate-pulse"></div>
                </div>
                <div class="h-4 w-16 bg-slate-700 rounded animate-pulse"></div>
              </div>
            </div>
          </div>

          <!-- Empty state -->
          <div
            v-else-if="!dashboardStats?.recent_registrations?.length"
            class="rounded-xl bg-slate-800/50 border border-slate-700 px-5 py-8 text-center"
          >
            <p class="text-sm text-slate-500">{{ t('dashboard.organizer.no_recent_registrations') }}</p>
          </div>

          <!-- Registration feed items -->
          <div v-else class="space-y-3">
            <article
              v-for="reg in dashboardStats.recent_registrations"
              :key="reg.id"
              class="rounded-xl bg-slate-800/50 border border-slate-700 px-5 py-4"
            >
              <div class="flex items-center gap-4">
                <!-- Participant initial avatar -->
                <div class="shrink-0 w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center">
                  <span class="text-sm font-bold text-slate-300">{{ registrationInitial(reg) }}</span>
                </div>

                <!-- Registration info -->
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-semibold text-white truncate">{{ reg.participant_name }}</p>
                  <p class="text-xs text-slate-400 truncate">
                    {{ reg.event_name }}
                    <span class="mx-1">&middot;</span>
                    {{ reg.race_name }}
                  </p>
                </div>

                <!-- Amount and time -->
                <div class="shrink-0 text-right">
                  <p class="text-sm font-semibold text-white">{{ formatCurrency(reg.total_amount) }}</p>
                  <p class="text-xs text-slate-500">{{ formatRelativeTime(reg.registered_at) }}</p>
                </div>
              </div>
            </article>
          </div>
        </section>

        <!-- Zone D: Recent cancellations feed -->
        <section class="mt-8" :aria-label="t('dashboard.organizer.recent_cancellations_title')">
          <h2 class="text-lg font-semibold text-white mb-4">{{ t('dashboard.organizer.recent_cancellations_title') }}</h2>

          <!-- Loading state -->
          <div v-if="statsLoading" class="space-y-3">
            <div
              v-for="n in 3"
              :key="n"
              class="rounded-xl bg-slate-800/50 border border-slate-700 px-5 py-4"
            >
              <div class="flex items-center gap-4">
                <div class="w-10 h-10 rounded-full bg-slate-700 animate-pulse"></div>
                <div class="flex-1 space-y-2">
                  <div class="h-4 w-32 bg-slate-700 rounded animate-pulse"></div>
                  <div class="h-3 w-48 bg-slate-700 rounded animate-pulse"></div>
                </div>
                <div class="h-4 w-20 bg-slate-700 rounded animate-pulse"></div>
              </div>
            </div>
          </div>

          <!-- Empty state -->
          <div
            v-else-if="!dashboardStats?.recent_cancellations?.length"
            class="rounded-xl bg-slate-800/50 border border-slate-700 px-5 py-8 text-center"
          >
            <p class="text-sm text-slate-500">{{ t('dashboard.organizer.no_recent_cancellations') }}</p>
          </div>

          <!-- Cancellation feed items -->
          <div v-else class="space-y-3">
            <article
              v-for="reg in dashboardStats.recent_cancellations"
              :key="reg.id"
              class="rounded-xl bg-amber-500/5 border border-amber-500/20 px-5 py-4"
            >
              <div class="flex items-center gap-4">
                <!-- Participant initial avatar -->
                <div class="shrink-0 w-10 h-10 rounded-full bg-amber-500/15 flex items-center justify-center">
                  <span class="text-sm font-bold text-amber-400">{{ cancellationInitial(reg) }}</span>
                </div>

                <!-- Cancellation info -->
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-semibold text-white truncate">{{ reg.participant_name }}</p>
                  <p class="text-xs text-slate-400 truncate">
                    {{ reg.event_name }}
                    <span class="mx-1">&middot;</span>
                    {{ reg.race_name }}
                  </p>
                </div>

                <!-- Refund info and time -->
                <div class="shrink-0 text-right">
                  <template v-if="parseFloat(reg.refunded_amount) > 0">
                    <p class="text-sm font-semibold text-amber-400">
                      &minus;{{ formatCurrency(reg.refunded_amount) }}
                    </p>
                    <p class="text-xs text-amber-500/70">{{ t('dashboard.organizer.cancellation_refunded') }}</p>
                  </template>
                  <template v-else-if="parseFloat(reg.refund_pending_amount) > 0">
                    <p class="text-sm font-semibold text-slate-300">
                      {{ formatCurrency(reg.refund_pending_amount) }}
                    </p>
                    <p class="text-xs text-slate-400">{{ t('dashboard.organizer.cancellation_refund_pending') }}</p>
                  </template>
                  <template v-else>
                    <p class="text-sm font-semibold text-slate-500">{{ t('dashboard.organizer.cancellation_no_refund') }}</p>
                  </template>
                  <p class="text-xs text-slate-500 mt-0.5">{{ formatRelativeTime(reg.cancelled_at) }}</p>
                </div>
              </div>
            </article>
          </div>
        </section>
      </template>
    </main>
  </OrganizerLayout>
</template>
